<template>
  <section class="rounded-lg border bg-card p-6">
    <div class="flex items-center justify-between gap-3">
      <h3 class="text-base font-semibold">
        {{ title }}
      </h3>
      <div v-if="isEditable && schedule" class="flex items-center gap-2">
        <Switch v-if="isEditable" :model-value="isActive" :disabled="!canToggleActive || isBusy" :title="isActive ? 'Pause schedule' : 'Activate schedule'" @update:model-value="toggleScheduleActive" />
        <Button type="button" size="icon" variant="outline" :loading="isSaving" :disabled="isBusy" :title="schedule ? 'Edit schedule' : 'Set schedule'" @click="openEditModal">
          <Pencil />
          <span class="sr-only">{{ schedule ? 'Edit schedule' : 'Set schedule' }}</span>
        </Button>
        <Button v-if="canClear" type="button" size="icon" variant="outline" class="text-destructive hover:text-destructive" :disabled="isBusy" title="Remove schedule" @click="removeSchedule">
          <Trash2 />
          <span class="sr-only">Remove schedule</span>
        </Button>
      </div>
    </div>

    <div v-if="schedule" class="mt-3 space-y-2 text-sm">
      <p><span class="font-medium">Recurrence:</span> {{ recurrenceLabel }}</p>
      <p><span class="font-medium">Starts At:</span> {{ formatDateTime(schedule.starts_at) }}</p>
      <p><span class="font-medium">Last Occurrence:</span> {{ formatDateTime(schedule.last_occurrence_at) }}</p>
    </div>

    <div v-else class="mt-6 flex min-h-32 items-center justify-center">
      <Button v-if="isEditable" type="button" @click="openEditModal">
        Create a Schedule
      </Button>
      <p v-else class="text-sm text-muted-foreground">
        No schedule configured.
      </p>
    </div>

    <ScheduleEditModal v-if="isEditable" ref="scheduleEditModal" :schedule="schedule" :is-saving="isSaving" :save-schedule="saveSchedule" />
  </section>
</template>

<script setup>
import { computed, ref } from 'vue'
import { toast } from 'vue-sonner'
import { Pencil, Trash2 } from 'lucide-vue-next'
import { Button } from '../../../../../../../resources/js/components/ui/button/index.js'
import { Switch } from '../../../../../../../resources/js/components/ui/switch/index.js'
import { useScheduleHumanReadable } from '../../composables/useScheduleHumanReadable.js'
import ScheduleEditModal from './ScheduleEditModal.vue'

const props = defineProps({
  title: {
    type: String,
    default: 'Schedule',
  },
  schedule: {
    type: Object,
    default: null,
  },
  isSaving: {
    type: Boolean,
    default: false,
  },
  isTogglingActive: {
    type: Boolean,
    default: false,
  },
  isEditable: {
    type: Boolean,
    default: true,
  },
  saveSchedule: {
    type: Function,
    default: null,
  },
  clearScheduleHandler: {
    type: Function,
    default: null,
  },
  toggleActiveHandler: {
    type: Function,
    default: null,
  },
})

const scheduleEditModal = ref(null)
const { toHumanReadableRrule } = useScheduleHumanReadable()
const isBusy = computed(() => props.isSaving || props.isTogglingActive)
const canClear = computed(() => {
  return props.schedule && typeof props.clearScheduleHandler === 'function'
})
const canToggleActive = computed(() => {
  return props.schedule && typeof props.toggleActiveHandler === 'function'
})
const isActive = computed(() => {
  const rawValue = props.schedule?.pivot?.is_active

  if (typeof rawValue === 'boolean') {
    return rawValue
  }

  if (typeof rawValue === 'number') {
    return rawValue === 1
  }

  if (typeof rawValue === 'string') {
    return rawValue === '1' || rawValue.toLowerCase() === 'true'
  }

  return true
})
const recurrenceLabel = computed(() => {
  const generatedName = props.schedule?.name

  if (typeof generatedName === 'string' && generatedName.trim() !== '') {
    return generatedName
  }

  return toHumanReadableRrule(props.schedule?.rrule)
})

const formatDateTime = (value) => {
  if (typeof value !== 'string' || value.trim() === '') {
    return '-'
  }

  const date = new Date(value)

  if (Number.isNaN(date.getTime())) {
    return '-'
  }

  return date.toLocaleString()
}

const openEditModal = () => {
  scheduleEditModal.value?.openModal?.()
}

const removeSchedule = async () => {
  if (!canClear.value) {
    return
  }

  try {
    await props.clearScheduleHandler()
    toast.success('Schedule removed.')
  } catch (error) {
    const errorMessage = error?.response?.data?.message || 'Unable to remove schedule.'
    toast.error(errorMessage)
  }
}

const toggleScheduleActive = async (nextValue) => {
  if (!canToggleActive.value || isBusy.value) {
    return
  }

  const normalizedNextValue = Boolean(nextValue)

  if (normalizedNextValue === isActive.value) {
    return
  }

  try {
    await props.toggleActiveHandler(normalizedNextValue)
    toast.success(normalizedNextValue ? 'Schedule activated.' : 'Schedule paused.')
  } catch (error) {
    const errorMessage = error?.response?.data?.message
      || error?.response?.data?.errors?.is_active?.[0]
      || 'Unable to update schedule status.'

    toast.error(errorMessage)
  }
}
</script>
